import { useEffect, useRef } from 'react';
import { Link, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import { useUser } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faUsers,
  faKey,
  faCreditCard,
  faBuilding,
  faToggleOn,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  ProfilePanel,
  APIKeysPanel,
  UsersPanel,
  StripePanel,
  AgenciesPanel,
  ServicesPanel,
  NotificationsPanel,
} from '../features/settings';

interface Section {
  path: string;
  label: string;
  icon: IconDefinition;
  adminOnly?: boolean;
}

const SECTIONS: Section[] = [
  { path: '/settings', label: 'Profile', icon: faUser },
  { path: '/settings/notifications', label: 'Notifications', icon: faBell },
  { path: '/settings/api-keys', label: 'API Keys', icon: faKey },
  { path: '/settings/users', label: 'Users', icon: faUsers },
  { path: '/settings/agencies', label: 'Agencies', icon: faBuilding },
  { path: '/settings/services', label: 'Services', icon: faToggleOn },
  { path: '/settings/stripe', label: 'Stripe', icon: faCreditCard, adminOnly: true },
];

function SectionChip({ section, active }: { section: Section; active: boolean }) {
  return (
    <Link
      to={section.path}
      data-active={active || undefined}
      // Taller on phones so each chip is a comfortable tap
      className="pressable flex-shrink-0 px-3 py-2 md:py-1.5"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid ' + (active ? 'var(--border-strong)' : 'transparent'),
        background: active ? 'var(--surface-active)' : 'transparent',
        color: active ? 'var(--text-body)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--text-sm)',
        fontWeight: (active
          ? 'var(--weight-medium)'
          : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        transition:
          'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
      }}
    >
      <FontAwesomeIcon icon={section.icon} style={{ fontSize: '12px', width: '14px' }} />
      {section.label}
    </Link>
  );
}

export default function Settings() {
  const { user } = useUser();
  const location = useLocation();

  const isActivePath = (path: string) => location.pathname === path;

  // Phones show the sections as one sideways-scrolling row; bring the current one into
  // view so a deep link to Stripe doesn't open with its chip off-screen.
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    const chip = nav?.querySelector<HTMLElement>('[data-active]');
    if (!nav || !chip || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2;
  }, [location.pathname]);

  return (
    <div className="min-h-screen">
      <Sidebar />
      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader title="Settings" />

        <div
          ref={navRef}
          className="relative flex flex-nowrap md:flex-wrap items-center overflow-x-auto md:overflow-x-visible scrollbar-hidden -mx-4 px-4 md:mx-0 md:px-0"
          style={{
            gap: '6px',
            padding: '0 0 10px',
            borderBottom: '1px solid var(--border-hairline)',
            marginBottom: 'var(--space-6)',
          }}
        >
          {SECTIONS.filter((s) => !s.adminOnly || user?.role === 'admin').map((section) => (
            <SectionChip key={section.path} section={section} active={isActivePath(section.path)} />
          ))}
        </div>

        <div className="ck-stagger">
          <Routes>
            <Route index element={<ProfilePanel />} />
            <Route path="notifications" element={<NotificationsPanel />} />
            <Route path="api-keys" element={<APIKeysPanel />} />
            <Route path="users" element={<UsersPanel />} />
            <Route path="agencies" element={<AgenciesPanel />} />
            <Route path="services" element={<ServicesPanel />} />
            <Route path="agents" element={<Navigate to="/agents" replace />} />
            <Route path="stripe" element={<StripePanel />} />
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
